import { useEffect, useState } from 'react'
import React from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { invoiceApi, accountApi, documentApi } from '@/services/api'
import type { Invoice, Account, Document } from '@/types'
import { FileText, Plus, Send, DollarSign, Ban, Trash2, Paperclip, File, Image } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'

function formatSize(bytes: number | null) {
  if (!bytes) return '—'
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function DocIcon({ mimeType }: { mimeType: string }) {
  if (mimeType.startsWith('image/')) return <Image className="w-3.5 h-3.5 text-[#0078C8] shrink-0" />
  if (mimeType === 'application/pdf') return <FileText className="w-3.5 h-3.5 text-[#CC0000] shrink-0" />
  return <File className="w-3.5 h-3.5 text-[#A6A9B0] shrink-0" />
}

const STATUS_TABS = ['all', 'draft', 'sent', 'paid', 'overdue', 'voided'] as const
type StatusTab = typeof STATUS_TABS[number]

const STATUS_VARIANT: Record<string, 'default' | 'info' | 'success' | 'destructive' | 'warning'> = {
  draft: 'default',
  sent: 'info',
  paid: 'success',
  overdue: 'destructive',
  voided: 'default',
}

function RecordPaymentModal({
  invoice, accounts, onClose, onDone,
}: {
  invoice: Invoice; accounts: Account[]; onClose: () => void; onDone: (updated: Invoice) => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({ payment_date: today, amount: invoice.amount_due, payment_account_id: '' })
  const [saving, setSaving] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const payload: { payment_date: string; amount: string; payment_account_id?: string } = {
        payment_date: form.payment_date, amount: form.amount,
      }
      if (form.payment_account_id) payload.payment_account_id = form.payment_account_id
      const res = await invoiceApi.recordPayment(invoice.id, payload)
      onDone(res.data.invoice)
      toast.success('Payment recorded')
      onClose()
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to record payment')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Record Payment — {invoice.invoice_number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <p className="text-[12px] text-[rgba(0,10,30,0.75)]">
            Amount due: <strong className="text-[#000A1E]">{formatCurrency(invoice.amount_due, invoice.currency)}</strong>
          </p>
          <div className="space-y-1">
            <Label>Payment Date</Label>
            <Input type="date" value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })} required />
          </div>
          <div className="space-y-1">
            <Label>Amount</Label>
            <Input type="number" step="0.01" min="0.01" value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
          </div>
          <div className="space-y-1">
            <Label>Payment Account <span className="text-[rgba(0,10,30,0.5)] font-normal">(optional)</span></Label>
            <select value={form.payment_account_id}
              onChange={(e) => setForm({ ...form, payment_account_id: e.target.value })} className="input">
              <option value="">— No GL entry —</option>
              {accounts.filter((a) => a.account_type === 'asset' && !a.is_archived).map((a) => (
                <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
              ))}
            </select>
          </div>
          <Separator />
          <div className="flex gap-3">
            <Button type="submit" disabled={saving} className="flex-1">
              {saving ? 'Recording...' : 'Record Payment'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default function InvoiceList() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState<Invoice[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [docs, setDocs] = useState<Document[]>([])
  const [activeTab, setActiveTab] = useState<StatusTab>('all')
  const [loading, setLoading] = useState(true)
  const [premiumGated, setPremiumGated] = useState(false)
  const [paymentInvoice, setPaymentInvoice] = useState<Invoice | null>(null)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [sending, setSending] = useState<string | null>(null)

  useEffect(() => {
    if (!clientId) return
    Promise.all([invoiceApi.list(clientId), accountApi.list(clientId), documentApi.list(clientId)])
      .then(([invRes, accRes, docRes]) => {
        setInvoices(invRes.data.invoices)
        setAccounts(accRes.data.accounts)
        setDocs(docRes.data.documents)
      })
      .catch((err: unknown) => {
        const status = (err as { response?: { status?: number } })?.response?.status
        if (status === 402) setPremiumGated(true)
        else toast.error('Failed to load invoices')
      })
      .finally(() => setLoading(false))
  }, [clientId])

  function docsFor(invoiceId: string) {
    return docs.filter((d) => d.linked_invoice_id === invoiceId)
  }

  const filtered = activeTab === 'all' ? invoices : invoices.filter((inv) => inv.status === activeTab)

  async function handleSend(inv: Invoice) {
    setSending(inv.id)
    try {
      const res = await invoiceApi.send(inv.id)
      setInvoices((prev) => prev.map((i) => (i.id === res.data.invoice.id ? res.data.invoice : i)))
      if (inv.contact_email) {
        toast.success(`${inv.invoice_number} sent to ${inv.contact_email}`)
      } else {
        toast.success('Invoice marked as sent')
      }
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    } finally {
      setSending(null)
    }
  }

  async function handleVoid(inv: Invoice) {
    if (!confirm(`Void invoice ${inv.invoice_number}?`)) return
    try {
      const res = await invoiceApi.void(inv.id)
      setInvoices((prev) => prev.map((i) => (i.id === res.data.invoice.id ? res.data.invoice : i)))
      toast.success('Invoice voided')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  async function handleDelete(inv: Invoice) {
    if (!confirm(`Delete draft invoice ${inv.invoice_number}?`)) return
    try {
      await invoiceApi.delete(inv.id)
      setInvoices((prev) => prev.filter((i) => i.id !== inv.id))
      toast.success('Invoice deleted')
    } catch (err: unknown) {
      toast.error((err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed')
    }
  }

  if (premiumGated) {
    return (
      <Card className="max-w-sm mx-auto mt-16">
        <CardContent className="p-10 text-center">
          <div className="w-12 h-12 bg-[#FFF3CD] rounded-[12px] flex items-center justify-center mx-auto mb-4">
            <FileText className="w-6 h-6 text-[#856404]" />
          </div>
          <h2 className="text-[17px] font-bold text-[#000A1E] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Invoicing requires a Premium client. Upgrade in Client Settings &rarr; Plan.
          </p>
          <Button onClick={() => navigate(`/clients/${clientId}/settings`)}>Upgrade to Premium</Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-5">
      {/* Xero page header */}
      <div className="flex items-center justify-between">
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Invoices</h1>
        <Button variant="create" onClick={() => navigate(`/clients/${clientId}/invoices/new`)}>
          <Plus className="w-4 h-4" /> New Invoice
        </Button>
      </div>

      {/* Xero-style status tabs */}
      <div className="flex gap-0 border-b-2 border-[#CCCED2]">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-3 text-[13px] transition-colors -mb-[2px] border-b-[3px]',
              activeTab === tab
                ? 'text-[#000A1E] font-bold border-b-[#0078C8]'
                : 'text-[#0078C8] font-normal border-b-transparent hover:text-[#000A1E]'
            )}
          >
            {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Content panel */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-5 space-y-3">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead className="text-right">Amount Due</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-[13px] text-[rgba(0,10,30,0.75)]">
                      No invoices found
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((inv) => (
                    <React.Fragment key={inv.id}>
                      <TableRow>
                        <TableCell>
                          <button
                            onClick={() => navigate(`/clients/${clientId}/invoices/${inv.id}/edit`)}
                            className="text-[13px] font-bold text-[#0078C8] hover:underline"
                          >
                            {inv.invoice_number}
                          </button>
                        </TableCell>
                        <TableCell>
                          <p className="text-[13px] text-[#000A1E]">{inv.contact_name}</p>
                          {inv.contact_email && <p className="text-[12px] text-[rgba(0,10,30,0.75)]">{inv.contact_email}</p>}
                        </TableCell>
                        <TableCell className="text-[rgba(0,10,30,0.75)]">{inv.issue_date}</TableCell>
                        <TableCell className="text-[rgba(0,10,30,0.75)]">{inv.due_date}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatCurrency(inv.total, inv.currency)}
                        </TableCell>
                        <TableCell className="text-right font-bold tabular-nums">
                          {formatCurrency(inv.amount_due, inv.currency)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[inv.status] ?? 'default'}>
                            {inv.status.charAt(0).toUpperCase() + inv.status.slice(1)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => setExpandedId(expandedId === inv.id ? null : inv.id)}
                              title="Attachments"
                            >
                              <Paperclip className={cn(
                                'w-3.5 h-3.5',
                                docsFor(inv.id).length > 0 ? 'text-[#0078C8]' : 'text-[#CCCED2]',
                              )} />
                            </Button>
                            {inv.status === 'draft' && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                disabled={sending === inv.id}
                                onClick={() => handleSend(inv)}
                                title={inv.contact_email ? `Send to ${inv.contact_email}` : 'Mark as Sent'}
                              >
                                <Send className={`w-3.5 h-3.5 ${inv.contact_email ? 'text-blue-500' : 'text-gray-400'}`} />
                              </Button>
                            )}
                            {(inv.status === 'sent' || inv.status === 'overdue') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => setPaymentInvoice(inv)} title="Record payment">
                                <DollarSign className="w-3.5 h-3.5 text-[#00823C]" />
                              </Button>
                            )}
                            {(inv.status === 'draft' || inv.status === 'sent') && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleVoid(inv)} title="Void invoice">
                                <Ban className="w-3.5 h-3.5 text-[#856404]" />
                              </Button>
                            )}
                            {inv.status === 'draft' && (
                              <Button variant="ghost" size="icon-sm" onClick={() => handleDelete(inv)} title="Delete"
                                className="hover:bg-[#F8D7DA] hover:text-[#CC0000]">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                      {expandedId === inv.id && (
                        <TableRow className="bg-[#F2F3F4] hover:bg-[#F2F3F4]">
                          <TableCell colSpan={8} className="px-6 py-3">
                            {docsFor(inv.id).length === 0 ? (
                              <p className="text-[12px] text-[rgba(0,10,30,0.75)]">No documents attached to this invoice.</p>
                            ) : (
                              <div className="flex flex-wrap gap-3">
                                {docsFor(inv.id).map((doc) => (
                                  <a
                                    key={doc.id}
                                    href={doc.file_path}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center gap-1.5 text-[12px] text-[#000A1E] hover:text-[#0078C8] bg-white border border-[#CCCED2] rounded-[3px] px-2.5 py-1.5 transition-colors"
                                  >
                                    <DocIcon mimeType={doc.mime_type} />
                                    <span className="font-bold truncate max-w-[160px]">{doc.file_name}</span>
                                    {doc.category && (
                                      <Badge variant="default" className="capitalize text-[10px] px-1 py-0">
                                        {doc.category}
                                      </Badge>
                                    )}
                                    <span className="text-[rgba(0,10,30,0.75)]">{formatSize(doc.file_size)}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {paymentInvoice && (
        <RecordPaymentModal
          invoice={paymentInvoice}
          accounts={accounts}
          onClose={() => setPaymentInvoice(null)}
          onDone={(updated) => {
            setInvoices((prev) => prev.map((i) => (i.id === updated.id ? updated : i)))
            setPaymentInvoice(null)
          }}
        />
      )}
    </div>
  )
}
